import OnPlatePosition from "./OnPlatePosition.js";

export default class Plate {
    /**
     * @param {Matrix3D} matrix3d
     * @param {number} level
     */
    constructor(matrix3d, level) {
        this._matrix3d = matrix3d;
        this._meta = {level: level, rows: matrix3d.meta.rows, cols: matrix3d.meta.cols};
        this._injectionIdToCoorinatesMap = this._buildIdToCoordinatesMap(matrix3d, level);
        this._currentSelection = new Set();
    }

    /** @return {{level: number, rows: number, cols: number}} */
    get meta() {
        return this._meta;
    }

    /**
     * @param {string} id
     * @return {boolean}
     */
    includes(id){
        return this._injectionIdToCoorinatesMap.has(id);
    }

    /** @return {string[]} */
    getInjectionIds() {
        return [...this._injectionIdToCoorinatesMap.keys()];
    }

    /**
     * @param {OnPlatePosition} pos
     * @return {InjectionMetaJson | InjectionRow | null}
     */
    getInjection(pos) {
        return this._matrix3d.getCell(pos, this._meta.level)
    }

    /**
     * @param injectionId
     * @return {OnPlatePosition}
     */
    getCoordinatesByInjectionId(injectionId) {
        return this._injectionIdToCoorinatesMap.get(injectionId);
    }

    /** @param {string[]} ids */
    setSelection(ids) {
        this._currentSelection.clear();
        for (const id of ids)
            if (this._injectionIdToCoorinatesMap.has(id))
                this._currentSelection.add(id)
    }

    /** @return {string[]} */
    getCurrentSelection() {
        return Array.from(this._currentSelection);
    }

    /**
     * @param {OnPlatePosition} from
     * @return {OnPlatePosition | null}
     */
    getLeft(from) {
        let startCol = from.colIndex - 1;
        let cell = null;
        search:
            for (let row = from.rowIndex; row >= 0; row--) {
                for (let col = startCol; col >= 0; col--) {
                    const pos = new OnPlatePosition(row, col);
                    if (this.getInjection(pos)) {
                        cell = pos;
                        break search;
                    }
                }
                startCol = this._matrix3d.meta.cols - 1;
            }
        return cell;
    }

    /**
     * @param {OnPlatePosition} from
     * @return {OnPlatePosition | null}
     */
    getRight(from) {
        let startCol = from.colIndex + 1;
        let cell = null;
        search:
            for (let row = from.rowIndex; row < this._matrix3d.meta.rows; row++) {
                for (let col = startCol; col < this._matrix3d.meta.cols; col++) {
                    const pos = new OnPlatePosition(row, col);
                    if (this.getInjection(pos)) {
                        cell = pos;
                        break search;
                    }
                }
                startCol = 0;
            }
        return cell;
    }

    /**
     * @param {OnPlatePosition} from
     * @return {OnPlatePosition | null}
     */
    getAbove(from) {
        let cell = null;
        for (let row = from.rowIndex - 1; row >= 0; row--) {
            const pos = new OnPlatePosition(row, from.colIndex);
            if (this.getInjection(pos)) {
                cell = pos;
                break;
            }
        }
        return cell;
    }

    /**
     * @param {OnPlatePosition} from
     * @return {OnPlatePosition | null}
     */
    getBelow(from) {
        let cell = null;
        for (let row = from.rowIndex + 1; row < this._matrix3d.meta.rows; row++) {
            const pos = new OnPlatePosition(row, from.colIndex);
            if (this.getInjection(pos)) {
                cell = pos;
                break;
            }
        }
        return cell;
    }

    /**
     * @param {Matrix3D} matrix3d
     * @param {number} level
     * @return {Map<string, OnPlatePosition>}
     * @private
     */
    _buildIdToCoordinatesMap(matrix3d, level) {
        const {rows, cols} = matrix3d.meta;
        const index = new Map();
        for (let i = 0; i < rows; i++) {
            for (let j = 0; j < cols; j++) {
                const pos = new OnPlatePosition(i, j);
                const injection = matrix3d.getCell(pos, level);
                if (injection) index.set(injection.getId(), pos);
            }
        }
        return index;
    }
}
